import React, { FC } from 'react';
import { Accordion, Container, ListGroup } from 'react-bootstrap';
import { Action, Button, Label } from '..';
import useReorder from '../../../hooks/useReorder/useReorder';
import styles from './SortableListItem.module.css';

export interface SubListItem {
  compactIndicatorId: string;
  id: string;
  title: string;
  sortOrder: number;
  description: string;
}

export interface ListItem {
  id: string;
  title: string;
  sortOrder: number;
  subListItems: SubListItem[];
}

interface SortableListItemProps {
  id: string;
  changeOrderEnabled: boolean;
  listItem: ListItem;
  listItems: ListItem[];
  listItemsLength: number;
  listItemIndex: number;
  onSubListItemsChangeHandler: (
    id: string,
    items: SubListItem[],
    from: number,
    to: number
  ) => void;
  moveListItem: (from: number, to: number) => void;
  handleDelete: (id: string) => void;
  handleEdit: (id: string) => void;
}

const SortableListItem: FC<SortableListItemProps> = ({
  id,
  changeOrderEnabled,
  listItem,
  listItems,
  listItemsLength,
  listItemIndex,
  onSubListItemsChangeHandler,
  moveListItem,
  handleDelete,
  handleEdit,
}) => {
  const handleSubListItemsChange = (
    subListItems: SubListItem[],
    from: number,
    to: number
  ) => {
    onSubListItemsChangeHandler(id, subListItems, from, to);
  };

  const [items, moveItem] = useReorder<SubListItem>(
    listItem.subListItems,
    handleSubListItemsChange,
    true
  );

  return (
    <ListGroup.Item>
      <Accordion flush alwaysOpen defaultActiveKey="0">
        <Accordion.Item eventKey="0">
          <Accordion.Header>
            <div className={styles.accordionHeader}>
              <Label content={listItem.title} size="heading-md" />
              <div className={styles.sortActionsWrapper}>
                {changeOrderEnabled &&
                  listItemsLength > 1 &&
                  listItemIndex !== 0 && (
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        moveListItem(
                          listItem.sortOrder,
                          listItems[listItemIndex - 1].sortOrder
                        );
                      }}
                    >
                      ↑
                    </Button>
                  )}
                {changeOrderEnabled &&
                  listItemsLength > 1 &&
                  listItemIndex !== listItemsLength - 1 && (
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        moveListItem(
                          listItem.sortOrder,
                          listItems[listItemIndex + 1].sortOrder
                        );
                      }}
                    >
                      ↓
                    </Button>
                  )}
              </div>
            </div>
          </Accordion.Header>
          <Accordion.Body>
            {items.length === 0 && <p>Nothing to show.</p>}
            {items.length > 0 &&
              items.map((item, index) => (
                <Container key={item.title} className={styles.indicatorWrapper}>
                  <div className={styles.titleDescription}>
                    <div>
                      <b>Indicator Title:</b> {item.title}
                    </div>
                    <div>
                      <b>Indicator Description:</b>{' '}
                      {item.description ? item.description : 'No Description'}
                    </div>
                  </div>
                  <div className={styles.actionsWrapper}>
                    <div className={styles.sortActionsWrapper}>
                      {changeOrderEnabled &&
                        items.length > 1 &&
                        index !== 0 && (
                          <Button
                            onClick={() =>
                              moveItem(
                                item.sortOrder,
                                items[index - 1].sortOrder
                              )
                            }
                          >
                            ↑
                          </Button>
                        )}
                      {changeOrderEnabled &&
                        items.length > 1 &&
                        index !== items.length - 1 && (
                          <Button
                            onClick={() =>
                              moveItem(
                                item.sortOrder,
                                items[index + 1].sortOrder
                              )
                            }
                          >
                            ↓
                          </Button>
                        )}
                    </div>
                    <div className={styles.crudActions}>
                      <Action
                        type="edit"
                        onClick={() => handleEdit(item.compactIndicatorId)}
                      />
                      <Action
                        type="delete"
                        onClick={() => handleDelete(item.compactIndicatorId)}
                      />
                    </div>
                  </div>
                </Container>
              ))}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </ListGroup.Item>
  );
};

export default SortableListItem;
